import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import { alignMaterials } from './alignMaterials';
import { getParts, groupOfCategory, isHardwareOrTool, type PartItem } from './partsRun';
import { getColourways, getSubmodels, type Submodel } from './listingStore';
import { surfaceKey } from './materialSurface';
import { getStandardMaterials } from './standardMaterials';
import { materialText, type MaterialPair } from './variantLabel';
import { followPartMaterials } from './productPieces';

// WHICH OF THE PRODUCT'S MATERIALS EACH PART IS MADE OF.
//
// Phase 1 answers what the PRODUCT is made of, per submodel: beige fabric with dark
// rubberwood, or grey fabric with a whitewashed one. Nothing yet says which of those each
// PART is — that the seat is the fabric and the backrest frame is the wood — and that is the
// fact a 3D model of a part has to be painted from, and the reason the Material menu on a
// mesh currently offers every wood the notice names and cannot say which is this part's.
//
// PER SUBMODEL, necessarily. The same seat is beige in one colourway and grey in another; a
// single answer for "the seat" would be true of one submodel and wrong about the others. So
// the choice is filed under (submodel, part), and the row reads whichever submodel is
// selected.
//
// A CHOICE AMONG PHASE 1'S OPTIONS, never free text: the listing's own materials, by their
// words. ANSWERED BY PHASE 5's materials reading, which judges how each material looks and which
// one each component is — it was a Phase 2 call of its own, answering for a phase that came three
// later. Every reading, once applied, reassigns every component (see replacePartMaterials); the Parts column of
// the materials table changes one by hand in between.
//
// COMPONENTS ONLY for the reading. A chipboard screw is steel and a felt pad is felt whatever
// the chair is made of: asking which of the product's two woods a screw is has no true answer,
// and a model asked anyway will pick one. The hardware is put on the default materials by hand.

// The pick for one part: the pair itself, or null for "none of them applies", which is a
// real answer. A part with no entry at all has never been asked.
export type PartMaterial = MaterialPair | null;

// By submodel id, then by part INDEX — the same keying as every other per-part answer in
// this phase, and it dies with the list the way the others do: a re-extraction renumbers the
// parts and invalidates answers about a list that no longer exists.
type BySub = Record<string, Record<number, PartMaterial>>;

type Snapshot = { bySub: BySub };

const key = (docId: string) => `notice.partMaterial.${docId}`;

const store = createDocEntryStore<Snapshot>((docId) => ({
  snapshot: { bySub: readState<BySub>(docId, key(docId), {}) },
}));

export const usePartMaterials = store.useSnapshot;
export const getPartMaterials = store.get;

// WHICH PARTS THE CALL IS ABOUT: the product's own components. The same test the listing
// crops row uses for the parts that are worth looking for in a photograph, and for the same
// underlying reason — a component IS the product, a screw is a screw.
export const wantsPartMaterial = (p: PartItem, category: string | null | undefined) =>
  !p.tool && groupOfCategory(category) === 'Components';

// The materials one submodel is made of, as Phase 1 read them and lined up by majority vote —
// what its LISTING says. The materials call's whole list: it asks only about the components,
// which are the product, and the product is made of what the listing names.
export function listingMaterials(submodels: Submodel[], subId: string): MaterialPair[] {
  const at = submodels.findIndex((s) => s.id === subId);
  return at < 0
    ? []
    : (alignMaterials(submodels)[at] ?? []).filter((p): p is MaterialPair => !!p);
}

// The Material MENU's list: the listing's materials, and the ones a part can be picked into by
// hand, from one list so a hand-picked value and a model-picked one come from the same words.
//
// AND THE ONES THIS APP STATES ITSELF, at the end, after what the listing said. A screw is
// not made of anything the listing names — it names what the PRODUCT is made of, and the
// product is wood and fabric — so without these the only honest answers for a barrel nut
// were "none of them" and silence. See standardMaterials for why those and no others.
//
// Last in the list because that is what they are: the listing's own words are what this
// notice is about, and a menu that opened with "Steel, zinc-plated" above the wood the chair
// is made of would be leading with the exception. Deduplicated, so a listing that genuinely
// names felt keeps its own wording rather than being shadowed by ours.
//
// A COMPONENT GETS THE LISTING'S ONLY — `component`. It is the product, and the product is made
// of what the listing names; the defaults are for the hardware, and a seat offered "Steel,
// zinc-plated" is a menu offering an answer that cannot be true.
export function materialOptions(
  docId: string, submodels: Submodel[], subId: string, component = false,
): MaterialPair[] {
  const listed = listingMaterials(submodels, subId);
  if (component) return listed;
  const taken = new Set(listed.map(surfaceKey));
  return [...listed, ...getStandardMaterials(docId).map((m) => m.pair)
    .filter((p) => p.material.trim() && !taken.has(surfaceKey(p)))];
}

// WHICH COLOURWAYS ONE PART'S ANSWER IS FILED UNDER. A component's is its colourway's own — the
// seat is beige in one and grey in the next. A piece of hardware's or a tool's is EVERY
// colourway's: a zinc-plated screw is the same screw whichever chair it goes into, so answering
// it in one answers it in all, and the next colourway opens with it answered (see shareHardware).
//
// Before the categories have landed everything reads as a component, so an early write stays in
// the one colourway it was made in — the safe side of the mistake.
const subsOf = (docId: string, subId: string, at: number): string[] => {
  const item = getParts(docId)[at];
  if (!item || !isHardwareOrTool(item)) return [subId];
  return [...new Set([subId, ...getColourways(docId).map((s) => s.id)])];
};

// ONE COLOURWAY'S WORD FOR A MATERIAL, IN ANOTHER. A standard material is everyone's and is the
// same word everywhere. One of the PRODUCT's materials is not: a wood button is finished like the
// chair, so it is on the dark wood in one colourway and the whitewashed one in the next — the
// listings lined up material by material (alignMaterials) say which is which. Undefined where
// the other colourway has no such material: nothing to file there. Null stays null.
const standardKeys = (docId: string) => new Set(getStandardMaterials(docId).map((m) => surfaceKey(m.pair)));
function inColourway(
  docId: string, pair: MaterialPair | null, from: string, to: string,
): MaterialPair | null | undefined {
  if (!pair || from === to || standardKeys(docId).has(surfaceKey(pair))) return pair;
  const subs = getSubmodels(docId);
  const aligned = alignMaterials(subs);
  const key = surfaceKey(pair);
  const at = (aligned[subs.findIndex((s) => s.id === from)] ?? []).findIndex((p) => !!p && surfaceKey(p) === key);
  return at < 0 ? undefined : (aligned[subs.findIndex((s) => s.id === to)]?.[at] ?? undefined);
}
// Whether `pair` is a word this colourway has — a standard material, or one of its own listing's.
function ownWord(docId: string, sub: string, pair: MaterialPair | null): boolean {
  if (!pair || standardKeys(docId).has(surfaceKey(pair))) return true;
  return listingMaterials(getSubmodels(docId), sub).some((p) => surfaceKey(p) === surfaceKey(pair));
}
// A tool's part names, keyed by material, re-keyed into another colourway's words.
function namesIn(docId: string, names: PartNames, pairs: MaterialPair[], from: string, to: string): PartNames {
  const out: PartNames = {};
  for (const pair of pairs) {
    const said = names[surfaceKey(pair)];
    const there = inColourway(docId, pair, from, to);
    if (said && there) out[surfaceKey(there)] = said;
  }
  return out;
}

// THE HARDWARE ANSWERED BEFORE THAT RULE, OR IN A COLOURWAY ADDED SINCE: each piece of hardware
// or tool a colourway has no answer for takes the one another colourway has — the first that has
// one, in the listing's order. A part answered in a colourway keeps its answer there — unless that
// answer is another colourway's word (a wood button carried across as the dark wood into the pale
// chair), which is answered again from a colourway whose word is its own, translated. Idempotent,
// so it runs whenever the parts or the colourways change.
export function shareHardware(docId: string): void {
  const subs = getColourways(docId).map((s) => s.id);
  const items = getParts(docId);
  if (subs.length < 2 || !items.length) return;
  const first = { ...store.get(docId).bySub };
  const also = { ...alsoStore.get(docId).bySub };
  const names = { ...namesStore.get(docId).bySub };
  let changed = false;
  items.forEach((item, at) => {
    if (!isHardwareOrTool(item)) return;
    // ON ONE OF THE PRODUCT'S OWN MATERIALS — a wood button on the frame's wood — it wears what
    // that component wears, in every colourway (see wornWith). Not translated by the listings'
    // order: the beige colourway lists its wood first, and the button came out in the seat's fabric.
    const with_ = wornWith(items, first, subs, at);
    if (with_ != null) {
      for (const sub of subs) {
        const there = first[sub]?.[with_];
        const had = first[sub]?.[at];
        if (!there || (had && surfaceKey(had) === surfaceKey(there))) continue;
        first[sub] = { ...(first[sub] ?? {}), [at]: there };
        changed = true;
      }
      return;
    }
    const right = (sub: string) => !!first[sub] && at in first[sub] && ownWord(docId, sub, first[sub][at])
      && (also[sub]?.[at] ?? []).every((p) => ownWord(docId, sub, p));
    const from = subs.find(right);
    if (from == null) return;
    for (const sub of subs) {
      if (right(sub)) continue;
      const there = inColourway(docId, first[from][at], from, sub);
      if (there === undefined) continue;
      first[sub] = { ...(first[sub] ?? {}), [at]: there };
      const rest = (also[from]?.[at] ?? []).map((p) => inColourway(docId, p, from, sub))
        .filter((p): p is MaterialPair => !!p);
      also[sub] = { ...(also[sub] ?? {}) };
      if (rest.length) also[sub][at] = rest; else delete also[sub][at];
      const had = [first[from][at], ...(also[from]?.[at] ?? [])].filter((p): p is MaterialPair => !!p);
      names[sub] = { ...(names[sub] ?? {}) };
      const said = namesIn(docId, names[from]?.[at] ?? {}, had, from, sub);
      if (Object.keys(said).length) names[sub][at] = said; else delete names[sub][at];
      changed = true;
    }
  });
  if (!changed) return;
  writeState(docId, key(docId), first);
  store.patch(docId, { bySub: first });
  writeState(docId, alsoKey(docId), also);
  alsoStore.patch(docId, { bySub: also });
  writeState(docId, namesKey(docId), names);
  namesStore.patch(docId, { bySub: names });
}

// WHICH COMPONENT A PIECE OF HARDWARE WEARS THE MATERIAL OF, where it wears one of theirs: the
// component whose material it shares in the most colourways — so one colourway answered wrong is
// outvoted, not copied. Null for hardware of its own material (steel, felt, plastic).
function wornWith(
  items: PartItem[], bySub: Record<string, Record<number, MaterialPair | null>>, subs: string[], at: number,
): number | null {
  let best: number | null = null;
  let most = 0;
  items.forEach((part, j) => {
    if (j === at || isHardwareOrTool(part) || part.supplied === false) return;
    const votes = subs.filter((sub) => {
      const mine = bySub[sub]?.[at];
      const theirs = bySub[sub]?.[j];
      return !!mine && !!theirs && surfaceKey(mine) === surfaceKey(theirs);
    }).length;
    if (votes > most) { most = votes; best = j; }
  });
  return best;
}

// WHAT A COLOURWAY'S MESHES ARE PAINTED IN, part by part: each component in its material, and each
// piece of hardware that wears one of the product's own materials (a wood button) in it too — its
// mesh is otherwise sent as it was built, in whichever wood that was (Louis 2026-10-28). Hardware
// of its own material keeps its own file's colours. `itemIdOf` is the part's catalog row.
export function partPaints(
  items: PartItem[], picks: Record<number, MaterialPair | null> | undefined,
  itemIdOf: (at: number) => number | null | undefined, only?: number[],
): { itemId: number; material: string }[] {
  const product = new Set(items
    .map((part, at) => (!isHardwareOrTool(part) && part.supplied !== false && picks?.[at] ? surfaceKey(picks[at]!) : null))
    .filter((k): k is string => !!k));
  return (only ?? items.map((_, at) => at))
    .filter((at) => items[at] && items[at].supplied !== false && picks?.[at]
      && (!isHardwareOrTool(items[at]) || product.has(surfaceKey(picks[at]!))))
    .map((at) => ({ itemId: itemIdOf(at), material: materialText(picks![at]!) }))
    .filter((p): p is { itemId: number; material: string } => p.itemId != null && !!p.material);
}

// One part's material, by hand. Written through immediately: it is a judgement somebody made
// by looking, and it costs more to make again than to keep.
export function setPartMaterial(
  docId: string, subId: string, at: number, pair: PartMaterial,
): void {
  const all = { ...store.get(docId).bySub };
  for (const sub of subsOf(docId, subId, at)) {
    const there = inColourway(docId, pair, subId, sub);
    if (there !== undefined) all[sub] = { ...(all[sub] ?? {}), [at]: there };
  }
  const mine = all[subId];
  writeState(docId, key(docId), all);
  store.patch(docId, { bySub: all });
  // NOT FOR A PIECE OF HARDWARE: it is in no piece of the whole product, whose meshes this would
  // rewrite from end to end — a dowel repicked spun Phase 5 for the length of a product apply,
  // changing nothing. Its own meshes are repainted where it is picked (see useMaterialParts).
  const item = getParts(docId)[at];
  if (item && isHardwareOrTool(item)) return;
  void followPartMaterials(docId, subId, mine, [at]);
}

// A RUN JUST KEPT, painted like the rest: the apply writes the kept runs only, so one kept after
// it wears whatever it was built with until this puts the applied reading on it. Every kept run
// of the colourway, pieces and all — a few files, not a strip's worth.
export function repaintKept(docId: string, subId: string): void {
  void followPartMaterials(docId, subId, store.get(docId).bySub[subId] ?? {});
}

// A TOOL IS MADE OF MORE THAN ONE THING — a steel blade in a plastic handle — and is the one kind
// of part that may be on several materials. Its FIRST is filed above with every other part's, so
// everything that asks "what is this part made of" still gets one answer; THE REST are here, by
// submodel and part like the first. A screw is steel; a seat is the fabric; nothing else has two.
type AlsoBySub = Record<string, Record<number, MaterialPair[]>>;
const alsoKey = (docId: string) => `notice.partMaterialAlso.${docId}`;
const alsoStore = createDocEntryStore<{ bySub: AlsoBySub }>((docId) => ({
  snapshot: { bySub: readState<AlsoBySub>(docId, alsoKey(docId), {}) },
}));
export const usePartMaterialsAlso = alsoStore.useSnapshot;

// Everything one part is made of, its first material first. Empty for a part never asked, or
// answered "none of them".
export function materialsOfPart(docId: string, subId: string, at: number): MaterialPair[] {
  const first = store.get(docId).bySub[subId]?.[at];
  return first ? [first, ...(alsoStore.get(docId).bySub[subId]?.[at] ?? [])] : [];
}

// ALL OF ONE PART'S MATERIALS, by hand: the first as setPartMaterial keeps it, the rest beside
// it. None is back to never-asked.
export function setPartMaterials(docId: string, subId: string, at: number, pairs: MaterialPair[]): void {
  if (pairs.length) setPartMaterial(docId, subId, at, pairs[0]);
  else clearPartMaterial(docId, subId, at);
  const all = { ...alsoStore.get(docId).bySub };
  let changed = false;
  for (const sub of subsOf(docId, subId, at)) {
    const mine = { ...(all[sub] ?? {}) };
    const rest = pairs.slice(1).map((p) => inColourway(docId, p, subId, sub))
      .filter((p): p is MaterialPair => !!p);
    if (rest.length) mine[at] = rest;
    else if (at in mine) delete mine[at];
    else continue;
    all[sub] = mine;
    changed = true;
  }
  if (!changed) return;
  writeState(docId, alsoKey(docId), all);
  alsoStore.patch(docId, { bySub: all });
}

// AND WHICH PART OF THE TOOL EACH IS — "blade", "handle" — by the material's own key. Said by the
// call that places it (see defaultTexturesRun) or typed in the Parts menu; read by the build, which
// is then told which mesh takes which name rather than left to judge it off the drawing.
export type PartNames = Record<string, string>;
type NamesBySub = Record<string, Record<number, PartNames>>;
const namesKey = (docId: string) => `notice.partMaterialOf.${docId}`;
const namesStore = createDocEntryStore<{ bySub: NamesBySub }>((docId) => ({
  snapshot: { bySub: readState<NamesBySub>(docId, namesKey(docId), {}) },
}));
export const usePartMaterialNames = namesStore.useSnapshot;
export const partNamesOf = (docId: string, subId: string, at: number): PartNames =>
  namesStore.get(docId).bySub[subId]?.[at] ?? {};

// Kept for the materials the part is on, and no others: one it has left takes its word with it.
export function setPartNames(docId: string, subId: string, at: number, names: PartNames): void {
  const on = new Set(materialsOfPart(docId, subId, at).map(surfaceKey));
  const kept = Object.fromEntries(
    Object.entries(names).map(([k, v]) => [k, v.trim()] as const).filter(([k, v]) => on.has(k) && v));
  const pairs = materialsOfPart(docId, subId, at);
  const all = { ...namesStore.get(docId).bySub };
  let changed = false;
  for (const sub of subsOf(docId, subId, at)) {
    const mine = { ...(all[sub] ?? {}) };
    const there = sub === subId ? kept : namesIn(docId, kept, pairs, subId, sub);
    if (Object.keys(there).length) mine[at] = there;
    else if (at in mine) delete mine[at];
    else continue;
    all[sub] = mine;
    changed = true;
  }
  if (!changed) return;
  writeState(docId, namesKey(docId), all);
  namesStore.patch(docId, { bySub: all });
}

// Forget one part's material, back to never-asked — which is not the same as "none of them
// applies" and must not be spelled the same way.
export function clearPartMaterial(docId: string, subId: string, at: number): void {
  const all = { ...store.get(docId).bySub };
  for (const sub of subsOf(docId, subId, at)) {
    const mine = { ...(all[sub] ?? {}) };
    delete mine[at];
    all[sub] = mine;
  }
  writeState(docId, key(docId), all);
  store.patch(docId, { bySub: all });
}

// WHAT A MATERIALS READING SAYS EVERY COMPONENT IS, all at once: each asked part gets the
// material the reading gave it, and one it left out is back to never-asked — a re-read that kept
// the last answer for a part it did not mention would be two readings speaking as one. Parts not
// asked (the hardware) keep theirs. One write, not one per part — and NO FOLLOW: its only caller
// is a reading being applied, whose apply moves the pieces and repaints (see the panel's apply).
// Answers what the parts are now.
export function replacePartMaterials(
  docId: string, subId: string, asked: number[], picks: Record<number, MaterialPair>,
): Record<number, PartMaterial> {
  const bySub = store.get(docId).bySub;
  const mine = { ...(bySub[subId] ?? {}) };
  for (const at of asked) {
    if (picks[at]) mine[at] = picks[at];
    else delete mine[at];
  }
  const all = { ...bySub, [subId]: mine };
  writeState(docId, key(docId), all);
  store.patch(docId, { bySub: all });
  return mine;
}
